import { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { APIContext } from 'context/APIContext'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import moment from 'moment'

import ContentFrame from 'components/Frame/ContentFrame'
import KinesisEventListItem from 'components/Athlete/v2/KinesisEventListItem'

export default function KinesisEventsList({ 
    boilerplateEnabled = 0,
    profileType = 'group',
    groupId = null,
    athleteId = null,
    allowEditing = true,
    kinesisEvents = [], 
    NavigateCalendarToSpecificWeek = () => {},
    ReloadTrainingChartData = () => {}
}) {

    const tabOptions = [
        {
            label: 'Past',
            value: 'past'
        },
        {
            label: 'Upcoming',
            value: 'upcoming'
        }
    ]

    const { SaveKinesisProfileEvents, DuplicateProgram, GetARaceProgramStartDate, StartProgramWithEventData, EditKinesisProfileEvent, ArchiveKinesisProfileEvent, RegenerateActiveProgramVolume } = useContext(APIContext)
    const { ShowModal, HideModal } = useContext(ModalContext)

    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)

    const [selectedTab, setSelectedTab] = useState('upcoming')
    const [filteredKinesisEvents, setFilteredKinesisEvents] = useState(null)

    const OnAddEventButton = () => {
        if (boilerplateEnabled === 0) {
            __OpenEventManagementModal()
        }

        if (boilerplateEnabled === 1) {
            __OpenCreateEventV5Modal()
        }
    }

    const OnEditEventButton = (kinesisEvent) => {
        if (boilerplateEnabled === 0) {
            __OpenEventManagementModal()
        }
        
        if (boilerplateEnabled === 1) {
            __OpenEditEventV5Modal(kinesisEvent)
        }
    }

    const __OpenEventManagementModal = () => {
        const modalData = {
            key: ModalKey.EventManagement,
            size: 'fullscreen',
            profileType: profileType,
            groupId: groupId,
            athleteId: athleteId,
            modalKinesisEvents: kinesisEvents,
            ReloadTrainingChartData: ReloadTrainingChartData
        }

        ShowModal(modalData)
    }

    const __OpenCreateEventV5Modal = () => {
        const modalData = {
            key: ModalKey.KinesisEventV5,
            size: 'third',
            mode: 'create-event',
            SaveEvent: __SaveKinesisEventModalAction
        }

        ShowModal(modalData)
    }

    const __SaveKinesisEventModalAction = async (submittedEvent) => {

        let newEvent = submittedEvent

        const kinesisUserGroupId = profileType === 'group' ? groupId : null
        const userId = profileType === 'individual' ? athleteId : null
        const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

        // An A race's program starts on its training start date, moved to the Monday after any program the profile already has
        // (running or upcoming) so the two never overlap

        if (newEvent.eventType === 'a-race') {

            const aRaceProgramStartDate = await GetARaceProgramStartDate(kinesisUserGroupId, userId, newEvent.eventTrainingStartDate, newEvent.eventDate)

            if (aRaceProgramStartDate === null) {
                return 'Failed to check when this A race\'s program can start. Please try again.'
            }

            if (aRaceProgramStartDate.valid === false) {
                return aRaceProgramStartDate.message
            }

            newEvent = { ...newEvent, eventTrainingStartDate: aRaceProgramStartDate.startDate }
        }

        const kinesisEventSaved = await SaveKinesisProfileEvents(kinesisUserGroupId, userId, [newEvent])

        if (kinesisEventSaved === false) {
            return 'Failed to save event. Please try again.'
        }

        // A races get their own duplicated program started from the event data
        if (newEvent.eventType === 'a-race') {

            const newProgramId = await DuplicateProgram('kinesisProfile', null, kinesisUserGroupId, userId, newEvent.sportId)

            if (newProgramId === null) {
                return 'Event saved, but failed to create a program for it.'
            }

            const programStarted = await StartProgramWithEventData(kinesisUserGroupId, userId, newProgramId, newEvent)

            if (programStarted === false) {
                console.error('Failed to start program ' + newProgramId + ' with event ' + newEvent.name + '!!')
                return 'Event saved, but failed to start its program.'
            }
        }

        // B and c races are taper overrides inside the active program, so regenerate its volume
        if (newEvent.eventType === 'b-race' || newEvent.eventType === 'c-race') {
            await RegenerateActiveProgramVolume(kinesisUserGroupId, userId, clientDate)
        }

        window.location.reload()

        HideModal()
    }

    const __OpenEditEventV5Modal = (kinesisEvent) => {
        const modalData = {
            key: ModalKey.KinesisEventV5,
            size: 'third',
            mode: 'edit-event',
            kinesisEvent: kinesisEvent,
            SaveEvent: (editedEvent) => __EditKinesisEventModalAction(kinesisEvent, editedEvent),
            DeleteEvent: () => __DeleteKinesisEventModalAction(kinesisEvent)
        }

        ShowModal(modalData)
    }

    const __EditKinesisEventModalAction = async (kinesisEvent, editedEvent) => {

        const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

        // An A race can't be moved inside another A race's program (its own program is not in the way)

        if (kinesisEvent.eventType === 'a-race') {

            const kinesisUserGroupId = profileType === 'group' ? groupId : null
            const userId = profileType === 'individual' ? athleteId : null
            const aRaceProgramStartDate = await GetARaceProgramStartDate(kinesisUserGroupId, userId, kinesisEvent.eventTrainingStartDate, editedEvent.eventDate, kinesisEvent.id)

            if (aRaceProgramStartDate === null) {
                return 'Failed to check this A race\'s dates. Please try again.'
            }

            if (aRaceProgramStartDate.valid === false) {
                return aRaceProgramStartDate.message
            }

            if (aRaceProgramStartDate.clamped === true) {
                return `This A race would overlap another A race's program, which runs until ${moment(aRaceProgramStartDate.blockingProgramEndDate).format('MMM D, YYYY')} (including its post race deload).`
            }
        }

        const kinesisEventEdited = await EditKinesisProfileEvent(kinesisEvent.id, kinesisEvent.name, kinesisEvent.eventType, kinesisEvent.eventTrainingStartDate, editedEvent.eventDate, 1)

        if (kinesisEventEdited === false) {
            return 'Failed to save event. Please try again.'
        }

        await RegenerateActiveProgramVolume(groupId, athleteId, clientDate)

        window.location.reload()

        HideModal()
    }

    const __DeleteKinesisEventModalAction = async (kinesisEvent) => {

        const clientDate = moment().local().add(simulationClockDayOffset, 'days').format('YYYY-MM-DD')

        const kinesisEventArchived = await ArchiveKinesisProfileEvent(groupId, athleteId, kinesisEvent.id)

        if (kinesisEventArchived === false) {
            return 'Failed to delete event. Please try again.'
        }

        await RegenerateActiveProgramVolume(groupId, athleteId, clientDate)

        window.location.reload()

        HideModal()
    }

    useEffect(() => {
        const todayStart = moment().startOf('day') // Already in local time

        if (selectedTab === 'upcoming') {
            setFilteredKinesisEvents(kinesisEvents.filter((event) => !moment(event.eventDate, 'YYYY-MM-DD', true).isBefore(todayStart, 'day')))
        } else if (selectedTab === 'past') {
            setFilteredKinesisEvents(kinesisEvents.filter((event) => moment(event.eventDate, 'YYYY-MM-DD', true).isBefore(todayStart, 'day')))
        } else {
            setFilteredKinesisEvents(kinesisEvents)
        }
    }, [kinesisEvents, selectedTab])

    if (filteredKinesisEvents === null) {
        return (
            <div className="h-full flex flex-col rounded-lg border border-gray-400 min-h-0">
                <div className='text-center py-2 border-b border-gray-400 flex-shrink-0'>
                    Races and Events
                </div>

                <div className='flex'>
                    {tabOptions.map((tab, tabIndex) => (
                        <div 
                            key={tabIndex}
                            className={`
                                py-2 flex-1 text-sm text-center cursor-pointer
                                ${selectedTab === tab.value ? '' : 'text-gray-400'}
                                ${selectedTab === tab.value ? 'bg-white' : 'bg-gray-100'}
                            `} 
                            onClick={() => setSelectedTab(tab.value)}
                        >
                            {tab.label}
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <ContentFrame 
            className="h-full min-h-0"
            titleText="Events"
            titleIcon={allowEditing ? <PlusCircleIcon className='h-5 w-5 text-gray-600 hover:text-gray-500' /> : null}
            titleAction={allowEditing ? OnAddEventButton : null}
        >
            <div className='w-full flex'>
                {tabOptions.map((tab, tabIndex) => (
                    <div 
                        key={tabIndex}
                        className={`
                            py-2 flex-1 text-xs 2xl:text-sm text-center cursor-pointer
                            ${selectedTab === tab.value ? '' : 'text-gray-400'}
                            ${selectedTab === tab.value ? 'bg-white' : 'bg-gray-100'}
                        `} 
                        onClick={() => setSelectedTab(tab.value)}
                    >
                        {tab.label}
                    </div>
                ))}
            </div>

            <div className="w-full flex-1 overflow-y-auto">
                {filteredKinesisEvents.length === 0 && 
                    <div className="py-4 text-center text-gray-500 text-xs 2xl:text-sm">No events found</div>
                }

                {filteredKinesisEvents.map((kinesisEvent) => (
                    <KinesisEventListItem 
                        key={kinesisEvent.id}
                        kinesisEvent={kinesisEvent} 
                        OnEventItemClick={() => { if (allowEditing) OnEditEventButton(kinesisEvent) }}
                        NavigateCalendarToSpecificWeek={NavigateCalendarToSpecificWeek}
                    />
                ))}
            </div>
        </ContentFrame>
    )
}